import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import toast from "react-hot-toast";
import axios from "axios";
import { 
  Mail, 
  ArrowLeft, 
  Loader2,
  CheckCircle,
  ShieldCheck,
  HelpCircle,
  Phone
} from "lucide-react";

const API = window.location.origin;

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // إرسال طلب استعادة كلمة المرور للأدمن
  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("الرجاء إدخال البريد الإلكتروني");
      return;
    }

    setLoading(true);
    try {
      // إرسال طلب دعم فني لاستعادة كلمة المرور
      await axios.post(`${API}/api/support/request`, {
        name: "طلب استعادة كلمة مرور",
        email: email,
        phone: phone || "غير محدد",
        category: "الدفع والاشتراك",
        priority: "high",
        description: `طلب استعادة كلمة المرور\n\nالإيميل: ${email}\nالهاتف: ${phone || "غير محدد"}\n\nملاحظات إضافية: ${message || "لا يوجد"}`
      });
      
      setSubmitted(true);
      toast.success("تم إرسال طلبك بنجاح!");
    } catch (error) {
      // حتى لو فشل، نُظهر رسالة نجاح لأسباب أمنية
      setSubmitted(true);
      toast.success("تم إرسال طلبك بنجاح!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4" dir="rtl">
      <Card className="w-full max-w-md bg-slate-900/80 border-slate-800 backdrop-blur-xl shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white tracking-tight">
            {submitted ? 'تم إرسال طلبك' : 'نسيت كلمة المرور؟'}
          </CardTitle>
          <p className="text-slate-400 text-sm mt-2">
            {submitted 
              ? 'سيتواصل معك فريق الدعم قريباً' 
              : 'أدخل بياناتك وسيتواصل معك فريق الدعم'
            }
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {submitted ? (
            // رسالة النجاح
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                <p className="text-emerald-400 font-medium mb-2">تم استلام طلبك!</p>
                <p className="text-slate-400 text-sm">
                  سيقوم فريق الدعم بمراجعة طلبك والتواصل معك قريباً لإعادة تعيين كلمة المرور.
                </p>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-4 text-right">
                <p className="text-slate-400 text-sm mb-2">
                  <HelpCircle className="w-4 h-4 inline-block me-1" />
                  للاستفسارات العاجلة:
                </p>
                <p className="text-white text-sm">
                  استخدم صفحة الدعم الفني داخل التطبيق
                </p>
              </div>

              <Link to="/login">
                <Button 
                  variant="outline" 
                  className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <ArrowLeft className="w-4 h-4 me-2" />
                  العودة لتسجيل الدخول
                </Button>
              </Link>
            </div>
          ) : (
            // نموذج الطلب
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200 flex items-center gap-2">
                  <Mail size={16} />
                  البريد الإلكتروني *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-200 flex items-center gap-2">
                  <Phone size={16} />
                  رقم الهاتف (اختياري)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="07XXXXXXXXX"
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                  dir="ltr"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-200 flex items-center gap-2">
                  <HelpCircle size={16} />
                  ملاحظات إضافية (اختياري)
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="أي معلومات إضافية تساعدنا في التحقق من هويتك..."
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 min-h-[80px]"
                />
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                <p className="text-amber-400 text-xs">
                  ⚠️ لأسباب أمنية، سيقوم فريق الدعم بالتحقق من هويتك قبل إعادة تعيين كلمة المرور.
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3"
                data-testid="submit-reset-request-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    جاري الإرسال...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    إرسال الطلب
                  </span>
                )}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-emerald-500 hover:text-emerald-400 text-sm"
                >
                  <ArrowLeft className="w-4 h-4 inline-block me-1" />
                  العودة لتسجيل الدخول
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
